import { useId, useCallback, useEffect, useRef, useState } from 'react';

import { useLockScroll, useOnEscape } from '@hivebrite/react-hooks';

import getFocusableElements from 'hb-react/shared/utils/getFocusableElements';
import overlaysList, {
  overlaysListPush,
  overlaysListSet,
} from 'hb-react/shared/utils/overlaysList';

import { type UseModalFn } from './types';

/** Auto-select the first focusable element outside the modal */
const focusFirstModalElement = (focusableElements: Array<HTMLElement>) => {
  const [firstFocusableElement] = focusableElements;

  firstFocusableElement.focus();
  firstFocusableElement.blur();
};

export const useModal: UseModalFn = ({
  isFocusOnRef,
  onCloseModal,
  enableEscAction,
}) => {
  const wrapperModalRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isScrollDown, setIsScrollDown] = useState(false);

  /** Ref of the last active element before the modal was opened */
  const lastActiveElementRef = useRef<Element | HTMLElement | null>(null);

  /** Create a unique ID for the modal */
  const labelledById = useId();

  useLockScroll({ enabled: isFocusOnRef, ref: viewportRef, scrollTo: [0, 0] });

  useEffect(() => {
    /** Add UUID of modal to list for tracking of focus handle */
    overlaysListPush(labelledById);

    /** Remember last focused element before modal was opened */
    lastActiveElementRef.current = document.activeElement;

    if (isFocusOnRef) {
      const focusableElements = getFocusableElements(wrapperModalRef.current);

      focusFirstModalElement(focusableElements);
    }

    return () => {
      /** Remove UUID of modal from list for tracking of focus handle */
      overlaysListSet(overlaysList.items.filter((id) => id !== labelledById));

      /** Return focus to the last active element */
      if (lastActiveElementRef.current?.tagName === 'BUTTON') {
        /** If the last active element was a button, focus its parent */
        const parentNode = lastActiveElementRef.current?.parentNode;

        if (parentNode instanceof HTMLElement) {
          const parentNodeHasTabIndex =
            parentNode?.getAttribute('tabindex') !== null;

          /** `tabIndex` is required to set focus on a node */
          if (!parentNodeHasTabIndex) {
            parentNode.setAttribute('tabindex', '-1');
          }

          parentNode?.focus({
            preventScroll: true,
          });

          if (!parentNodeHasTabIndex) {
            parentNode?.removeAttribute('tabindex');
          }
        }
      } else {
        const el = lastActiveElementRef.current;

        if (el instanceof HTMLElement) {
          el.focus({ preventScroll: true });
        }
      }
    };
  }, [isFocusOnRef, labelledById]);

  const handleCloseModal = useCallback(() => {
    if (enableEscAction) {
      onCloseModal();
    }
  }, [enableEscAction, onCloseModal]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      /** Disable tab events when another modal is opened */
      if (overlaysList.items[overlaysList.items.length - 1] !== labelledById) {
        return null;
      }

      const focusableElements = getFocusableElements(wrapperModalRef.current);

      const [firstFocusableElement] = focusableElements;

      const lastFocusableElement =
        focusableElements[focusableElements.length - 1];

      if (e.key === 'Tab') {
        /** When the active element does not belong to the focusable elements */
        if (
          !focusableElements.find((node) => node === document.activeElement)
        ) {
          e.preventDefault();

          firstFocusableElement.focus();
        }

        /** When focus firstElement and Shift+Tab = Focus last element */
        if (e.shiftKey && document.activeElement === firstFocusableElement) {
          e.preventDefault();

          lastFocusableElement.focus();
        }

        /** When focus lastElement and Tab = Focus first element */
        if (!e.shiftKey && document.activeElement === lastFocusableElement) {
          e.preventDefault();

          firstFocusableElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [labelledById, wrapperModalRef]);

  useEffect(() => {
    const el = viewportRef.current;

    if (!el) {
      return;
    }

    const handleScroll = () => {
      setIsScrollDown(el.scrollTop > 0);
    };

    el.addEventListener('scroll', handleScroll);

    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useOnEscape(handleCloseModal);

  return { labelledById, viewportRef, wrapperModalRef, isScrollDown };
};
